import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Trophy, ShoppingCart } from 'lucide-react';
import { useRfqComparativo, useRfqDetalhe } from '@/features/compras/hooks/useRfq';
import { useRfq } from '@/features/compras/hooks/useRfq';
import type { AdjudicarRfqRequest } from '@/features/compras/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ProdutoNome } from '@/components/ProdutoNome';
import { PessoaNome } from '@/components/PessoaNome';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';

const CotacaoComparativo = () => {
  const navigate = useNavigate();
  const { id }   = useParams<{ id: string }>();
  const idNum    = Number(id);

  const { data: rfq,   isLoading }  = useRfqComparativo(idNum);
  const { data: detalhe }           = useRfqDetalhe(idNum);
  const { adjudicarRfq, isAdjudicando } = useRfq();

  const [adjDialogOpen,    setAdjDialogOpen]    = useState(false);
  const [fornVencedorId,   setFornVencedorId]   = useState(0);
  const [gerarPO,          setGerarPO]          = useState(true);
  const [prazoPagamento,   setPrazoPagamento]   = useState(30);

  if (isLoading) return <div className="p-6 text-muted-foreground">Carregando comparativo...</div>;
  if (!rfq)      return <div className="p-6 text-muted-foreground">Cotação não encontrada.</div>;

  const forns = rfq.fornecedores.filter(f => f.respondeu);

  const handleAdjudicar = async () => {
    if (!fornVencedorId) return;
    const payload: AdjudicarRfqRequest = {
      fornecedorVencedorId: fornVencedorId,
      gerarOrdemCompra: gerarPO,
      prazoPagamentoDias: prazoPagamento,
    };
    await adjudicarRfq({ id: idNum, data: payload });
    setAdjDialogOpen(false);
    navigate(`/compras/cotacoes/${idNum}`);
  };

  const melhorPorItem = (itemId: number) => {
    let melhorForn = forns[0];
    let melhorPreco = Infinity;
    for (const forn of forns) {
      const item = forn.itens.find(i => i.rfqItemId === itemId);
      if (item && item.custoUnitario < melhorPreco) {
        melhorPreco = item.custoUnitario;
        melhorForn  = forn;
      }
    }
    return melhorForn?.fornecedorId;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/compras/cotacoes/${idNum}`)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Comparativo — RFQ #{rfq.numero}</h1>
            <p className="text-muted-foreground text-sm">{rfq.titulo}</p>
          </div>
        </div>
        {detalhe?.status === 'Encerrada' && (
          <Button className="gap-2 bg-green-600 hover:bg-green-700"
            onClick={() => setAdjDialogOpen(true)}>
            <Trophy className="h-4 w-4" />Adjudicar Vencedor
          </Button>
        )}
      </div>

      {forns.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8 text-muted-foreground">
            Nenhum fornecedor respondeu a esta cotação.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Tabela Comparativa de Preços</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-40">Produto</TableHead>
                  <TableHead>Qtd</TableHead>
                  {forns.map(f => (
                    <TableHead key={f.fornecedorId} className="text-center min-w-36">
                      <div>{f.nomeFornecedor ?? <PessoaNome id={f.fornecedorId} comCodigo={false} />}</div>
                      {f.isVencedor && <Badge className="bg-green-500/10 text-green-600 text-xs mt-1">Vencedor</Badge>}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rfq.itens.map(item => {
                  const melhorId = melhorPorItem(item.id);
                  return (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">
                        <div className="text-sm"><ProdutoNome id={item.produtoId} /></div>
                        {item.descricaoComplementar && (
                          <div className="text-xs text-muted-foreground">{item.descricaoComplementar}</div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{item.quantidade}</TableCell>
                      {forns.map(forn => {
                        const proposta = forn.itens.find(i => i.rfqItemId === item.id);
                        const isMelhor = forn.fornecedorId === melhorId;
                        return (
                          <TableCell key={forn.fornecedorId} className="text-center">
                            {proposta ? (
                              <div className={isMelhor ? 'font-bold text-green-600' : ''}>
                                <div className="text-sm font-mono">
                                  {proposta.custoUnitario.toLocaleString('pt-BR', {
                                    style: 'currency', currency: 'BRL',
                                  })}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Total: {proposta.totalItem.toLocaleString('pt-BR', {
                                    style: 'currency', currency: 'BRL',
                                  })}
                                </div>
                                {isMelhor && <Badge className="bg-green-500/10 text-green-600 text-xs">Menor preço</Badge>}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}

                {/* Totais */}
                <TableRow className="bg-muted/30 font-bold">
                  <TableCell colSpan={2}>Total Proposta</TableCell>
                  {forns.map(forn => (
                    <TableCell key={forn.fornecedorId} className="text-center font-mono">
                      {forn.valorTotalProposta.toLocaleString('pt-BR', {
                        style: 'currency', currency: 'BRL',
                      })}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Frete */}
                <TableRow>
                  <TableCell colSpan={2} className="text-sm text-muted-foreground">Frete</TableCell>
                  {forns.map(forn => (
                    <TableCell key={forn.fornecedorId} className="text-center text-sm font-mono">
                      {forn.valorFrete.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Prazo de entrega */}
                <TableRow>
                  <TableCell colSpan={2} className="text-sm text-muted-foreground">Prazo Entrega (dias)</TableCell>
                  {forns.map(forn => (
                    <TableCell key={forn.fornecedorId} className="text-center text-sm">
                      {forn.prazoEntregaDias ?? '—'}
                    </TableCell>
                  ))}
                </TableRow>

                {/* Prazo de pagamento */}
                <TableRow>
                  <TableCell colSpan={2} className="text-sm text-muted-foreground">Prazo Pagto (dias)</TableCell>
                  {forns.map(forn => (
                    <TableCell key={forn.fornecedorId} className="text-center text-sm">
                      {forn.prazoPagamentoDias ?? '—'}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialog de adjudicação */}
      <Dialog open={adjDialogOpen} onOpenChange={setAdjDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjudicar Vencedor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Fornecedor Vencedor *</Label>
              <div className="space-y-2">
                {forns.map(f => (
                  <div
                    key={f.fornecedorId}
                    className={`flex items-center justify-between p-3 border rounded cursor-pointer transition-colors ${
                      fornVencedorId === f.fornecedorId
                        ? 'border-green-500 bg-green-500/5'
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => setFornVencedorId(f.fornecedorId)}>
                    <span className="font-medium">{f.nomeFornecedor ?? <PessoaNome id={f.fornecedorId} comCodigo={false} />}</span>
                    <span className="font-mono text-sm">
                      {f.valorTotalProposta.toLocaleString('pt-BR', {
                        style: 'currency', currency: 'BRL',
                      })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={gerarPO} onCheckedChange={setGerarPO} id="gerar-po" />
              <Label htmlFor="gerar-po">Gerar Ordem de Compra automaticamente</Label>
            </div>
            {gerarPO && (
              <div className="space-y-2">
                <Label>Prazo de Pagamento (dias)</Label>
                <Input type="number" value={prazoPagamento}
                  onChange={e => setPrazoPagamento(Number(e.target.value))} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjDialogOpen(false)}>Cancelar</Button>
            <Button
              className="gap-2"
              disabled={!fornVencedorId || isAdjudicando}
              onClick={handleAdjudicar}>
              {gerarPO
                ? <><ShoppingCart className="h-4 w-4" />Adjudicar e Gerar PO</>
                : <><Trophy className="h-4 w-4" />Adjudicar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CotacaoComparativo;
